import { AMRAP_TEST_FLOOR, type WeightSource } from '@heraion/shared';

import type { LoggerMode } from './loggerMode';
import type {
  AmrapTestDraft,
  DraftSet,
  LoggerAction,
} from './state/loggerDraftTypes';

// #539: what one tap on the AMRAP chip writes, pure. The hook
// (useAmrapToggle) works out the offer and dispatches; this decides.
//
// On: the INTENTION goes on. On an ordinary pending set the reps cell empties
// and its value becomes the rep minimum (LG-28: a number already written turns
// into the answer); with a proposal the load is the test's too. A prescribed
// AMRAP keeps its load and reps (Antenore 04/11: «la prescrizione AMRAP del
// programma vince sul calcolatore»).
// Off: the intention goes off, and a pending set gets back what the chip
// replaced, field by field: only a field still equal to what the chip wrote.
// A weight or reps typed after the tap stay (D8).

type SetPatch = Extract<LoggerAction, { type: 'update_set' }>['patch'];

/** The test load, already in the set's unit: `weight` for the cell, `load`
 *  and `max` formatted for the tip. */
export type AmrapProposal = {
  kind: 'proposal';
  weight: string;
  source: WeightSource;
  basisKg: number;
  pct: number;
  load: string;
  max: string;
};

/** Why no load was proposed: bodyweight or assisted, a calibration or
 *  dropset set (the load is kept), or no instrument to read a max from. */
export type AmrapRefusal = {
  kind: 'refusal';
  reason: 'bodyweight' | 'kept' | 'noBasis';
};

export type AmrapOffer = AmrapProposal | AmrapRefusal;

/** An i18n key and its values; the hook translates it. */
export type AmrapTip = { key: string; params?: Record<string, string | number> };

const PROPOSAL_TIP: Partial<Record<WeightSource, string>> = {
  'effort-from-tested-max': 'mobile.logger.amrapTestFromTestedMax',
  'effort-from-max': 'mobile.logger.amrapTestFromMax',
  'effort-from-history': 'mobile.logger.amrapTestFromLastSet',
};

const REFUSAL_TIP: Record<AmrapRefusal['reason'], string> = {
  bodyweight: 'mobile.logger.amrapTestNoLoadBodyweight',
  kept: 'mobile.logger.amrapTestNoLoadKept',
  noBasis: 'mobile.logger.amrapTestNoLoadNoBasis',
};

const beforeOf = (set: DraftSet): AmrapTestDraft['before'] => ({
  weight: set.weight,
  reps: set.reps,
  ...(set.weightSource ? { weightSource: set.weightSource } : {}),
  ...(set.weightFellBack ? { weightFellBack: true as const } : {}),
});

const turnOn = (
  set: DraftSet,
  offer: AmrapOffer | null,
): { patch: SetPatch; tip: AmrapTip | null } => {
  const pending = set.completedAt === null;
  if (!pending || set.amrapPrescribed) return { patch: { amrap: true }, tip: null };
  // A dropset's reps live in its chain: the parent cell is not the minimum.
  if (set.dropset !== null) {
    return {
      patch: { amrap: true },
      tip: offer?.kind === 'refusal' ? { key: REFUSAL_TIP[offer.reason] } : null,
    };
  }
  const before = beforeOf(set);
  if (offer?.kind === 'proposal') {
    const floor = String(AMRAP_TEST_FLOOR);
    return {
      patch: {
        amrap: true,
        weight: offer.weight,
        reps: '',
        weightSource: offer.source,
        weightFellBack: undefined,
        amrapTest: {
          floor,
          applied: { weight: offer.weight, reps: '' },
          before,
          source: offer.source,
          basisKg: offer.basisKg,
          pct: offer.pct,
        },
      },
      tip: {
        key: PROPOSAL_TIP[offer.source] ?? 'mobile.logger.amrapTestFromLastSet',
        params: { load: offer.load, pct: offer.pct, max: offer.max, floor },
      },
    };
  }
  return {
    patch: {
      amrap: true,
      reps: '',
      amrapTest: {
        floor: set.reps.trim(),
        applied: { weight: set.weight, reps: '' },
        before,
      },
    },
    tip: offer ? { key: REFUSAL_TIP[offer.reason] } : null,
  };
};

const turnOff = (set: DraftSet): { patch: SetPatch; tip: AmrapTip | null } => {
  const test = set.amrapTest;
  if (!test) return { patch: { amrap: false }, tip: null };
  // A logged set keeps what was done: only the test's memory goes.
  if (set.completedAt !== null) {
    return { patch: { amrap: false, amrapTest: undefined }, tip: null };
  }
  const { applied, before } = test;
  const weightBack =
    applied.weight !== before.weight && set.weight === applied.weight;
  const repsBack = applied.reps !== before.reps && set.reps === applied.reps;
  return {
    patch: {
      amrap: false,
      amrapTest: undefined,
      ...(weightBack
        ? {
            weight: before.weight,
            weightSource: before.weightSource,
            weightFellBack: before.weightFellBack,
          }
        : {}),
      ...(repsBack ? { reps: before.reps } : {}),
    },
    tip:
      weightBack || repsBack ? { key: 'mobile.logger.amrapTestRestored' } : null,
  };
};

/** The patch one AMRAP tap writes, and the tip it shows (or null). In
 *  prescribe the chip is the coach's request, as before #539. */
export function amrapTogglePatch(
  set: DraftSet,
  mode: LoggerMode,
  offer: AmrapOffer | null,
): { patch: SetPatch; tip: AmrapTip | null } {
  // PE-42: in PRESCRIBE the same key carries the REQUEST (prescribeDispatch
  // writes it to CellDraft.amrap); the flip reads the field the chip shows.
  if (mode === 'prescribe') {
    return { patch: { amrap: !set.amrapPrescribed }, tip: null };
  }
  return set.amrap ? turnOff(set) : turnOn(set, offer);
}
